/* This component is to render barChart it expects the data in the format:
const barChartData = [
  { name: 'Jan 1', electricMeterValue: 400, electricSolarValue: 200 },
  { name: 'Feb 1', electricMeterValue: 300, electricSolarValue: 500 },
];
It will show the bars on the graph only when the data is passed in the above way and also the xAxis dataKey is set to name */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
    Bar,
    BarChart,
    CartesianGrid,
    Cell,
    Label,
    Legend,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis
} from 'recharts';
import EmptyState from '../DesignSystem/EmptyState';
import { LegendIcon } from '../styles';
import { chartColors } from './chartHelpers';
import { ChartTooltipStyles, StyledLegendWrapper } from './styles';

const getColour = (index: number) => {
    const barChartColorsLength = chartColors.length;
    return chartColors[index % barChartColorsLength];
};

const transformData = (barChartData: any) => {
    if (!!!barChartData?.length) {
        return [];
    }

    const allPropertyNames: string[] = [];

    barChartData.forEach((item: any) => {
        Object.keys(item).forEach((propertyName: string) => {
            if (propertyName !== 'name' && !allPropertyNames.includes(propertyName)) {
                allPropertyNames.push(propertyName);
            }
        });
    });

    return barChartData.map((item: any) => {
        const transformedItem: Record<string, number> = {
            name: item.name
        };

        allPropertyNames.forEach((propertyName: string) => {
            transformedItem[propertyName] = item[propertyName] || 0;
        });

        return transformedItem;
    });
};

const CustomTooltip = ({ active, payload }: any) => {
    if (active && payload && payload.length) {
        return payload.map((item: any) => (
            <ChartTooltipStyles>
                <div className='label'>{item.payload.name}</div>
                <p>
                    {item.name} : {item.value}
                </p>
            </ChartTooltipStyles>
        ));
    }
    return null;
};

const getBars = ({
    barChartData,
    stackedBarChart,
    chartData,
    onClick,
    setActiveIndex,
    activeIndex
}: any) => {
    const barCharts: any = [];
    if (barChartData) {
        Object.entries(barChartData)
            .filter(([key]) => (stackedBarChart ? key !== 'name' : key === 'value'))
            .forEach(([item]: any, index: any) => {
                barCharts.push(
                    <Bar
                        dataKey={item}
                        key={index + 'bar'}
                        fill={getColour(index)}
                        barSize={50}
                        stackId={stackedBarChart ? 'stackbar' : undefined}
                        onClick={onClick && onClick}
                        onMouseEnter={(e, index) => setActiveIndex(index)}
                        onMouseLeave={() => setActiveIndex(-1)}
                    >
                        {!stackedBarChart &&
                            chartData?.map((_entry: any, index: number) => (
                                <Cell
                                    key={`cell-${index}`}
                                    fill={getColour(index)}
                                    stroke={index === activeIndex ? '#4B74BE' : 'none'}
                                    strokeWidth={4}
                                />
                            ))}
                    </Bar>
                );
            });
    }
    return barCharts;
};

const getStackedBarLegendData = (barChartData: any, stackedBarChart: boolean) => {
    if (stackedBarChart)
        return Object.keys(transformData(barChartData)?.[0] || {})
            .filter((key) => key !== 'name')
            .map((key) => ({ name: key }));
    return barChartData;
};

const CustomizedXAxisLabel = (props: any) => {
    const { t } = useTranslation();
    const { x, y, payload, width, visibleTicksCount } = props;

    const labelWidth = width / visibleTicksCount;

    return (
        <g transform={`translate(${x},${y})`}>
            <foreignObject
                x={-labelWidth / 2}
                y={0}
                width={labelWidth - (5 * visibleTicksCount) / 2}
                height={30}
            >
                <div
                    title={payload.value}
                    style={{
                        fontSize: '14px',
                        width: '100%',
                        height: '100%',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        cursor: 'pointer',
                        textAlign: 'center',
                        marginRight: '5px'
                    }}
                >
                    {t(payload.value)}
                </div>
            </foreignObject>
        </g>
    );
};

const formatYAxisTick = (tick: any) => {
    if (tick >= 1000000) {
        return `${tick / 1000000}M`; // Display in millions
    } else if (tick >= 1000) {
        return `${tick / 1000}K`; // Display in thousands
    } else {
        return tick; // For values less than 1000
    }
};

const BarCharts = (props: any) => {
    const { t } = useTranslation();
    const {
        containerHeight = 400,
        chartTitle = '',
        barChartData,
        yAxisTickCount = 5,
        yAxisLabel = '',
        xAxisLabel = '',
        xAxisInterval = 0,
        stackedBarChart,
        showXAxisLabel = true,
        onClick,
        styles
    } = props;

    const [activeIndex, setActiveIndex] = useState(-1);
    const [isExpanded, setIsExpanded] = useState(false);
    if (!barChartData.length) {
        return <EmptyState />;
    }

    return (
        <div style={{ width: '100%', height: !isExpanded ? containerHeight : 600, ...styles }}>
            <ResponsiveContainer>
                <BarChart
                    data={barChartData}
                    margin={{
                        top: 40,
                        right: 20,
                        left: -20,
                        bottom: 20
                    }}
                >
                    <text
                        x={'50%'}
                        y={10}
                        fill='black'
                        textAnchor='middle'
                        dominantBaseline='central'
                    >
                        <tspan fontSize='20'>{chartTitle}</tspan>
                    </text>
                    <CartesianGrid vertical={false} />
                    <XAxis
                        padding={{ left: 15, right: 10 }}
                        hide={!showXAxisLabel}
                        dataKey={'name'}
                        interval={xAxisInterval}
                        tick={<CustomizedXAxisLabel />}
                    >
                        <Label value={xAxisLabel} position='bottom' offset={0} />
                    </XAxis>
                    <YAxis
                        axisLine={false}
                        tickLine={false}
                        tickCount={yAxisTickCount}
                        width={90}
                        tickFormatter={(tickLabel) => formatYAxisTick(tickLabel)}
                    >
                        <Label
                            value={yAxisLabel}
                            angle={-90}
                            style={{ textAnchor: 'middle', fill: 'black' }}
                            position='center'
                            dx={-10}
                            offset={1}
                        />
                    </YAxis>
                    <Tooltip
                        content={<CustomTooltip />}
                        cursor={{ fill: 'transparent' }}
                        shared={false}
                        wrapperStyle={{
                            outline: 'none'
                        }}
                    />
                    <Legend
                        align='right'
                        verticalAlign='bottom'
                        content={() => {
                            const legendData = getStackedBarLegendData(
                                barChartData,
                                stackedBarChart
                            );
                            return (
                                <>
                                    {legendData.length > 12 && (
                                        <div className='d-flex justify-content-end'>
                                            <i
                                                className={`fa ps-2 me-4 fa-angle-${
                                                    isExpanded ? 'up' : 'down'
                                                }`}
                                                aria-hidden='true'
                                                onClick={() => setIsExpanded(!isExpanded)}
                                            />
                                        </div>
                                    )}
                                    <StyledLegendWrapper
                                        className='d-flex flex-wrap col-12'
                                        style={
                                            !isExpanded
                                                ? { marginTop: 20, height: 100, overflow: 'hidden' }
                                                : {}
                                        }
                                    >
                                        {legendData.map((entry: any, index: number) => (
                                            <div className='item d-flex align-items-center col-3'>
                                                <LegendIcon color={getColour(index)} />
                                                <p
                                                    className='m-0'
                                                    title={entry.name}
                                                    key={`item-${index}`}
                                                >
                                                    {t(entry.name)}
                                                </p>
                                            </div>
                                        ))}
                                    </StyledLegendWrapper>
                                </>
                            );
                        }}
                    />
                    {getBars({
                        barChartData: transformData(barChartData)?.[0],
                        stackedBarChart,
                        chartData: barChartData,
                        onClick,
                        setActiveIndex,
                        activeIndex
                    })}
                </BarChart>
            </ResponsiveContainer>
        </div>
    );
};

export default BarCharts;
